import { Id } from "@highnote/server/src/core/entities";
import { useGlobalSpaceTracks } from "./useGlobalSpaceTracks";

/**
 * True while the space's `space-tracks-v1` rows have not answered yet. The
 * watcher is keyed by the space id (`useChildEntityWatchers`).
 *
 * NO WATCHER AT ALL also reads as "not yet answered". On a cold deep link
 * the space document arrives first, and its arrival is what TRIGGERS the row
 * watcher's attach — `useSpace` attaches it from an effect gated on
 * `space?.id`, so the space becoming truthy does not accompany the attach, it
 * causes it. For that one commit the map holds no watcher and the store holds
 * no row. Reading that as "answered, and this space has no row for the track"
 * is what let `TrackContextProvider` open the canonical `tracks-v2` listener
 * for a borrowed track, which the origin-only rules deny.
 *
 * A track context mounted with an EXPLICIT viewing space (the version editor
 * in `Main`) must not wait on this: the store may legitimately hold no
 * watcher for that space, and waiting would block its canonical listener for
 * good. `TrackContextProvider` excludes that mount from the wait with its
 * `!viewingSpaceId` term.
 */
export const useSpaceTrackRowsLoading = (spaceId?: Id): boolean => {
  const { globalSpaceTracksWatchers } = useGlobalSpaceTracks();
  if (!spaceId) return false;
  const watcher = globalSpaceTracksWatchers.get(spaceId);
  // Not attached yet — the rows have not answered, so nothing may conclude
  // that this space has no row for the track.
  if (!watcher) return true;
  return Array.from(watcher.loading?.values() ?? []).some(Boolean);
};
